import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { NumberCountry } from 'src/app/core/constants/number-city.constant';
import { TypeSecurityReference } from 'src/app/core/constants/type-security-references';
import { City, CommonOptions, DaneCity, SecurityReference, ExtractedDocumentResponse } from 'src/app/core/interfaces/interfaces';
import { CommonService } from 'src/app/core/services/modules/common.service';
import { EmployerService } from 'src/app/core/services/modules/employer.service';
import { PersonalService } from 'src/app/core/services/modules/personal.service';
import { DialogService } from 'src/app/core/services/utils/dialog.service';
import { FechasService } from 'src/app/core/services/utils/fechas.service';
import { SnackBarService } from 'src/app/core/services/utils/snackBar.service';
import { DocumentExtractionMapper } from 'src/app/core/utils/document-extraction-mapper';


export interface DialogData {
  StartDate: string; FinishDate: string; id: any; CompanyInfoId: any;
  DocumentNumber?: string; DocumentType?: string; IsRequest: boolean; typeContract: string;
  extractedData?: ExtractedDocumentResponse;
}

@Component({
  selector: 'app-dialog-create-person-info-company',
  templateUrl: './dialog-create-person-info-company.component.html',
  styleUrls: ['./dialog-create-person-info-company.component.scss']
})
export class DialogCreatePersonInfoCompanyComponent implements OnInit {
  imgCropperResult: string;
  formPersonal: FormGroup;
  formDataPhoto: FormData;

  jobs: any[] = [];
  rh: CommonOptions[] = [];
  sex: CommonOptions[] = [];
  daneCity: DaneCity[] = [];
  securityReferences: any = [];
  citiesNacimiento: City[] = [];
  afp: SecurityReference[] = [];
  eps: SecurityReference[] = [];
  countrys: CommonOptions[] = [];
  nationality: CommonOptions[] = [];
  companyDocumentType: CommonOptions[] = [];

  maxCaracter = 11;

  lowConfidenceFields = new Set<string>();
  warningMessages: string[] = [];
  formLocked = false;

  // Fecha de nacimiento deja de ser obligatoria para contratos VI/FU (ver ngOnInit, donde se limpia su validador).
  private readonly shortContractOptionalFields = ['BirthDate'];

  // Sexo, RH, país y ciudad de nacimiento no son obligatorios en ningún tipo de contrato.
  private readonly requiredFieldDefs: Array<{ key: string; label: string }> = [
    { key: 'DocumentType', label: 'Tipo de documento' },
    { key: 'DocumentNumber', label: 'Número de documento' },
    { key: 'Name', label: 'Nombres' },
    { key: 'LastName', label: 'Apellidos' },
    { key: 'Nationality', label: 'Nacionalidad' },
    { key: 'BirthDate', label: 'Fecha de nacimiento' },
  ];

  constructor(
    public dialogRef: MatDialogRef<DialogCreatePersonInfoCompanyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: FormBuilder,
    private commonService: CommonService,
    private snackBService: SnackBarService,
    private fechasService: FechasService,
    private dialogService: DialogService,
    private employerService: EmployerService,
    private personalService: PersonalService,
  ) {
  }

  ngOnInit(): void {
    this.servicesPersonal();
    this.formPersonal = this.fb.group({
        Address: new FormControl(''),
        AfpId: new FormControl(''),
        BirthDate: new FormControl('', Validators.required),
        BirthDateSelect: new FormControl(''),
        CityBirthCode: new FormControl(''),
        contryNalcionality: new FormControl(''),
        CityCode: new FormControl(''),
        CompanyInfoId: new FormControl(this.data.CompanyInfoId, Validators.required),
        DocumentNumber: new FormControl('', Validators.required),
        DocumentType: new FormControl('', Validators.required),
        EmergencyContact: new FormControl(''),
        EmergencyContactPhone: new FormControl(''),
        EpsId: new FormControl(''),
        JobCode: new FormControl(''),
        LastName: new FormControl('', Validators.required),
        Name: new FormControl('', Validators.required),
        Nationality: new FormControl('', Validators.required),
        PhoneNumber: new FormControl(''),
        RH: new FormControl(''),
        Sex: new FormControl(''),
        Email: new FormControl(''),
    });

    if (this.isShortContract) {
      this.shortContractOptionalFields.forEach(controlName => {
        this.formPersonal.get(controlName).clearValidators();
        this.formPersonal.get(controlName).updateValueAndValidity();
      });
    }

    if (this.data.DocumentNumber) {
      this.formPersonal.get('DocumentType').setValue(this.data.DocumentType);
      this.formPersonal.get('DocumentNumber').setValue(this.formatDocumentNumberValue(this.data.DocumentNumber));
      if (this.formPersonal.get('DocumentType').value) {
        this.formPersonal.get('Nationality').setValue('0');
      }
    }
  }

  private formatDocumentNumberValue(value: string | null | undefined): string {
    const digits = (value || '').toString().replace(/\D/g, '').slice(0, this.maxCaracter);
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  formatDocumentNumber(event: any, formGroup: FormGroup, controlName: string) {
    const input: HTMLInputElement = event.target;
    const digitsFromEndBefore = input.value.length - (input.selectionStart || 0);
    const formatted = this.formatDocumentNumberValue(input.value);

    input.value = formatted;
    formGroup.get(controlName)?.setValue(formatted, { emitEvent: false });

    const newPosition = Math.max(formatted.length - digitsFromEndBefore, 0);
    input.setSelectionRange(newPosition, newPosition);
  }

  get isShortContract(): boolean {
    return this.data.typeContract === 'VI' || this.data.typeContract === 'FU';
  }

  get photoRequired(): boolean {
    return !this.isShortContract;
  }

  private isFieldRequired(controlName: string): boolean {
    return this.requiredFieldDefs.some(field => field.key === controlName)
      && !(this.isShortContract && this.shortContractOptionalFields.includes(controlName));
  }

  private isFieldEmpty(controlName: string): boolean {
    const value = this.formPersonal?.get(controlName)?.value;
    return value === null || value === undefined || value === '';
  }

  // Lista, en lenguaje de usuario, de lo que falta para habilitar Guardar. Se basa en el valor real
  // de cada campo (no en control.invalid), porque un FormGroup deshabilitado (formLocked) reporta
  // invalid=false aunque tenga campos requeridos vacíos, lo que ocultaría el motivo real al usuario.
  get missingFieldLabels(): string[] {
    const missing: string[] = [];
    if (this.photoRequired && !this.formDataPhoto) {
      missing.push('Foto');
    }
    this.requiredFieldDefs.forEach(({ key, label }) => {
      if (this.isFieldRequired(key) && this.isFieldEmpty(key)) {
        missing.push(label);
      }
    });
    return missing;
  }

  canSubmit(): boolean {
    return this.missingFieldLabels.length === 0;
  }

  fieldClasses(controlName: string): { [key: string]: boolean } {
    const control = this.formPersonal?.get(controlName);
    return {
      'field-amber': this.lowConfidenceFields.has(controlName),
      'field-box--invalid': (this.isFieldRequired(controlName) && this.isFieldEmpty(controlName)) || (!!control && control.invalid)
    };
  }

  private applyExtractedData(extracted: ExtractedDocumentResponse): void {
    const flagLowConfidence = extracted.confianza !== 'alta' || (extracted.advertencias?.length || 0) > 0;
    // Con confianza alta bloqueamos SOLO los campos que la IA efectivamente completó, no el formulario
    // entero: así, si algún dato (ej. país/ciudad de nacimiento) no se pudo extraer, el campo queda
    // vacío pero editable para que el usuario lo complete a mano, en vez de quedar bloqueado y vacío.
    const lockExtractedFields = extracted.confianza === 'alta';
    this.warningMessages = extracted.advertencias || [];
    this.formLocked = lockExtractedFields;

    const setIfPresent = (controlName: string, value: any) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      this.formPersonal.get(controlName)?.setValue(value);
      if (flagLowConfidence) {
        this.lowConfidenceFields.add(controlName);
      }
      if (lockExtractedFields) {
        this.formPersonal.get(controlName)?.disable({ emitEvent: false });
      }
    };

    // El tipo/número mostrados deben ser los que efectivamente extrajo la IA del documento,
    // no los que se digitaron en la búsqueda (pueden diferir, ej. se buscó como Cédula pero el
    // documento real es un Permiso por Protección Temporal).
    if (extracted.numero) {
      setIfPresent('DocumentNumber', this.formatDocumentNumberValue(extracted.numero));
    }
    if (extracted.tipoDocumento && extracted.tipoDocumento !== 'DESCONOCIDO'
        && this.companyDocumentType.some(option => option.Value === extracted.tipoDocumento)) {
      setIfPresent('DocumentType', extracted.tipoDocumento);
    }

    setIfPresent('Name', extracted.nombres);
    setIfPresent('LastName', extracted.apellidos);
    setIfPresent('Sex', extracted.sexo);
    if (extracted.rh && this.rh.some(option => option.Value === extracted.rh)) {
      setIfPresent('RH', extracted.rh);
    }
    const birthDate = DocumentExtractionMapper.parseIsoDateLocal(extracted.fechaNacimiento);
    if (birthDate) {
      setIfPresent('BirthDate', birthDate);
    }

    const mappedNationality = DocumentExtractionMapper.mapGentilicioToNationality(extracted.nacionalidad, this.nationality);
    if (mappedNationality) {
      setIfPresent('Nationality', mappedNationality);
    }

    if (extracted.tipoDocumento === 'CC' || extracted.tipoDocumento === 'TI') {
      const colombia = this.countrys.find(country => (country.Description || '').toUpperCase().includes('COLOMB'));
      if (colombia) {
        setIfPresent('contryNalcionality', colombia.Value);
        this.commonService.getCities(colombia.Value).subscribe((cities: City[]) => {
          this.citiesNacimiento = cities;
          const parsedLugar = DocumentExtractionMapper.parseLugarNacimiento(extracted.lugarNacimiento);
          const matchedCity = parsedLugar ? DocumentExtractionMapper.matchCityByName(parsedLugar.city, cities) : null;
          if (matchedCity) {
            setIfPresent('CityBirthCode', matchedCity.Code);
          }
        });
      }
    } else if (!extracted.lugarNacimiento && (extracted.nacionalidad || '').toUpperCase().includes('VENEZ')) {
      // Documentos de venezolanos (PPT/CE) no traen lugarNacimiento; usamos la nacionalidad para
      // preseleccionar Venezuela como país y ciudad de nacimiento, solo cuando no hay dato real.
      const venezuela = this.countrys.find(country => (country.Description || '').toUpperCase().includes('VENEZ'));
      if (venezuela) {
        setIfPresent('contryNalcionality', venezuela.Value);
        this.commonService.getCities(venezuela.Value).subscribe((cities: City[]) => {
          this.citiesNacimiento = cities;
          const matchedCity = cities.find(city => (city.Name || '').toUpperCase().includes('VENEZ'));
          if (matchedCity) {
            setIfPresent('CityBirthCode', matchedCity.Code);
          }
        });
      }
    }
  }

  onSubmit() {
    let birthDate = this.fechasService.getDateFormatDayMontYear(this.formPersonal.get('BirthDate').value);

    if (this.isShortContract) {
      birthDate = '';
    } else if (this.formDataPhoto === undefined) {
      this.snackBService.openWarning('Por favor subir imagen de la persona', '', 4000);
      return;
    }

    if (this.canSubmit()) {
      const personal = {
        Address: this.formPersonal.get('Address').value,
        AfpId: this.formPersonal.get('AfpId').value,
        BirthDate: birthDate,
        BirthDateSelect: this.formPersonal.get('BirthDate').value,
        CityBirthCode: this.formPersonal.get('CityBirthCode').value,
        contryNalcionality: this.formPersonal.get('contryNalcionality').value,
        CityCode: this.formPersonal.get('CityCode').value,
        CompanyInfoId: this.formPersonal.get('CompanyInfoId').value,
        DocumentNumber: (this.formPersonal.get('DocumentNumber').value || '').toString().replace(/\D/g, ''),
        DocumentType: this.formPersonal.get('DocumentType').value,
        EmergencyContact: this.formPersonal.get('EmergencyContact').value,
        EmergencyContactPhone: this.formPersonal.get('EmergencyContactPhone').value,
        EpsId: this.formPersonal.get('EpsId').value,
        JobCode: 'D-0000.000',
        LastName: (this.formPersonal.get('LastName')?.value || '').toString().toUpperCase(),
        Name: (this.formPersonal.get('Name')?.value || '').toString().toUpperCase(),
        Nationality: this.formPersonal.get('Nationality').value,
        PhoneNumber: this.formPersonal.get('PhoneNumber').value,
        RH: this.formPersonal.get('RH').value,
        Sex: this.formPersonal.get('Sex').value,
        Email: this.formPersonal.get('Email').value
      };

      this.personalService.postPersonalInfoCompany(personal).subscribe((restPostPersonal: any) => {
        const pesonalCompany = {
          AfpId: restPostPersonal.AfpId,
          ArlId: 81,
          ContractId: this.data.id,
          EmployerId: null,
          EpsId: restPostPersonal.EpsId,
          FinishDate: this.data.IsRequest === false ?
            this.data.FinishDate : this.fechasService.getDateFormatDatePikerDATEHORANOWNotSecunt(),
          NameARL: 'ARL DESCONOCIDA',
          PersonalCompanyInfoId: restPostPersonal.PersonalCompanyInfoId,
          Position: '',
          StartDate: this.fechasService.getDateFormatDatePikerDATEHORANOWNotSecunt(),
          TypeARL: 'V'
        };

        if (this.data.typeContract !== 'VI' && this.data.typeContract !== 'FU') {
          this.personalService.updatePhotoCompanyInfo(restPostPersonal.Id, this.formDataPhoto).subscribe((rest: any) => {
            this.personalService.postContractPersonalInfo(this.data.id, pesonalCompany, this.data.IsRequest === false ?
              undefined : true).subscribe(restContractPersonal => {
              this.dialogRef.close({create: true, modelResponse: restContractPersonal, IsRequest: this.data.IsRequest});
            });
          });
        } else {
          this.personalService.postContractPersonalInfo(this.data.id, pesonalCompany, this.data.IsRequest === false ?
            undefined : true).subscribe(restContractPersonal => {
            this.dialogRef.close({create: true, modelResponse: restContractPersonal, IsRequest: this.data.IsRequest});
          });
        }
      });
    } else {
      this.snackBService.openWarning('Completar el formulario', '', 3000);
    }
  }

  convertirAMayusculas(event: any, formGroup: FormGroup, controlName: string) {
    const input: HTMLInputElement = event.target;
    const cursorPosition = input.selectionStart;
    const upperValue = (input.value || '').toUpperCase();

    input.value = upperValue;
    formGroup.get(controlName)?.setValue(upperValue, { emitEvent: false });
    input.setSelectionRange(cursorPosition, cursorPosition);
  }

  updatePhotoPersonal() {
    const adminId = this.data?.CompanyInfoId || '';

    this.dialogService.openDialogGestorImg('CARGAR IMAGEN', [], 'editPhoto', 0, 'true', true, undefined, false, [], adminId)
      .afterClosed().subscribe(async (result: any) => {
        if (!result) { return; }

        const MAX = 10_000_000;
        const base64: string | undefined = result?.imgResult;
        if (!base64 || typeof base64 !== 'string') {
          this.snackBService.openWarning('No se pudo obtener la imagen recortada', undefined, 4000);
          return;
        }

        const blob = this.base64ToBlob(base64);

        if (!blob || blob.size === 0) {
          this.snackBService.openWarning('No se pudo generar el archivo de la imagen', '', 4000);
          return;
        }

        if (blob.size > MAX) {
          this.snackBService.openWarning('El archivo supera el peso máximo permitido de 10 MB', '', 5000);
          return;
        }

        const fd = new FormData();
        fd.append('File', new File([blob], 'photo.jpg', { type: 'image/jpeg' }));

        this.imgCropperResult = base64;
        this.formDataPhoto = fd;
      });
  }

  private base64ToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header?.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] || 'image/jpeg';

    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mime });
  }

  servicesPersonal() {
    this.commonService.getJobs().subscribe((data: any) => {
      this.jobs = data;
    });
    this.employerService.getSecurityReferences().subscribe(data => {
      this.securityReferences = data;
      this.eps = this.securityReferences.filter(x => x.Type === TypeSecurityReference.EPS).sort((a, b) => a.OrderNum > b.OrderNum ? 1 : -1);
      this.afp = this.securityReferences.filter(x => x.Type === TypeSecurityReference.AFP).sort((a, b) => a.OrderNum > b.OrderNum ? 1 : -1);
    });
    this.getDaneCity();

    forkJoin({
      nationality: this.commonService.getCommonOptions('Nationality'),
      rh: this.commonService.getCommonOptions('RH'),
      companyDocumentType: this.commonService.getCommonOptions('CompanyDocumentType'),
      sex: this.commonService.getCommonOptions('Sex'),
      countrys: this.commonService.getCommonOptions('Country'),
    }).subscribe(({ nationality, rh, companyDocumentType, sex, countrys }) => {
      this.nationality = nationality;
      this.rh = rh.sort((a, b) => a.OrderNum > b.OrderNum ? 1 : -1);
      this.companyDocumentType = companyDocumentType.filter(documents => documents.Value === 'CC' ||
        documents.Value === 'CE' || documents.Value === 'PE' || documents.Value === 'PPT')
          .sort((a, b) => a.OrderNum > b.OrderNum ? 1 : -1);
      this.sex = sex.sort((a, b) => a.OrderNum > b.OrderNum ? 1 : -1);
      this.countrys = countrys;

      if (this.data.extractedData) {
        this.applyExtractedData(this.data.extractedData);
      }
    });
  }

  getDaneCity() {
    this.commonService.getDaneCity().subscribe((daneCity: DaneCity[]) => {
      this.daneCity = daneCity;
    });
  }

  getCitiesNacimiento(numberContry: NumberCountry){
    this.commonService.getCities(numberContry).subscribe((data: City[]) => {
      this.citiesNacimiento = data;
    });
  }

  changeType(e) {
    if (e === 'PE') {
      this.maxCaracter = 16;
    } else if (e === 'CC') {
      this.maxCaracter = 11;
    } else {
      this.maxCaracter = 20;
    }
  }

  close() {
    this.dialogRef.close();
  }

}
